/**
 * nj-writeback — pushes a New Journeys document back onto the object that OWNS
 * it, for the fields that object really has (operator ruling 2026-08-19:
 * "save onto the existing queue / event / appointment journey configs where a
 * row maps to one").
 *
 * What each origin can actually take today:
 *  • queue  — `updateQueue` (PATCH /v1/admin/queues/{id}) owns `name`,
 *    `heroImage` and `services`. Those three save onto the queue itself, so a
 *    rename or a service change made in the designer is visible in the queue
 *    console, the wizard and the public join page — not just here.
 *  • event  — stage 2 (build plan): the update op is NOT missing —
 *    `creation.updateEvent` (`@v0y4ge/api-client`) is the same op the wizard's
 *    own Publish step calls (`CreateEventPublish.tsx`), and it owns `title`
 *    today. The rest of Setup (details/schedule/tickets/guests) has no doc-side
 *    control to push from yet — that lands with the Setup views in stage 3, and
 *    each section writes through the op stage 1's field-ownership map already
 *    names (`updateEvent` / `updateEventTickets` / `updateEventRegistration` /
 *    `updateEventGuestList`), not through this function.
 *  • appt   — an appointment row is a LINE OF BUSINESS grouping of catalogue
 *    services, not a record with a journey field; `updateCatalogueService`
 *    writes one service, never the group's journey.
 *
 * Everything a queue or event has no column for — pages, blocks, the flow
 * graph, attachments, experiences — stays in the `newjourneys-api` overlay
 * until /v1/admin/newjourneys lands (see
 * CD-API-REQUEST-newjourneys-documents-2026-08-19.md). `writeBackDoc` reports
 * exactly which fields went to the object and what is still held in the
 * overlay, so the screen can say so rather than implying a save it didn't make.
 */
import { updateQueue } from "../queues/queues-api";
import { creation } from "@v0y4ge/api-client";
import type { Doc } from "./nj-model";
import { getOneStopPage, saveOneStopDraft } from "./onestop-page-api";
import { tzContext } from "../../lib/datetime";

export interface WriteBackResult {
  /** Human field names written onto the owning object. */
  wrote: string[];
  /** Why nothing was written, when `wrote` is empty. */
  held: string | null;
}

const NO_OP: Record<string, string> = {
  appt: "appointment rows group catalogue services and own no journey record — held in the journeys store",
  local: "this journey has no owning object yet — held in the journeys store",
};

/** The hero image the designer set on the first page that has one. */
function heroImage(doc: Doc): string | null {
  for (const pg of doc.pages) {
    const hero = pg.content.find((c) => c.type === "hero");
    const img = hero && typeof hero.img === "string" ? (hero.img as string) : "";
    if (img) return img;
  }
  return null;
}

/** The services picked on the queue's own service page. */
function queueServices(doc: Doc): string[] | null {
  const pg = doc.pages.find((x) => x.id === "services");
  const svc = pg?.svc as string[] | undefined;
  return Array.isArray(svc) ? svc.slice() : null;
}

async function writeBackQueue(doc: Doc, origin: NonNullable<Doc["origin"]>): Promise<WriteBackResult> {
  const patch: Record<string, unknown> = {};
  const wrote: string[] = [];
  if (doc.name && doc.name !== origin.label) { patch.name = doc.name; wrote.push("name"); }
  const img = heroImage(doc);
  if (img) { patch.heroImage = img; wrote.push("hero image"); }
  const svc = queueServices(doc);
  if (svc && svc.length) { patch.services = svc; wrote.push("services"); }
  if (!wrote.length) return { wrote: [], held: null };

  try {
    await updateQueue(origin.id, patch as any);
    return { wrote, held: null };
  } catch {
    /* The overlay already holds the edit, so nothing is lost — say it plainly. */
    return { wrote: [], held: "the queue rejected the write — the edit is held in the journeys store" };
  }
}

async function writeBackEvent(doc: Doc, origin: NonNullable<Doc["origin"]>): Promise<WriteBackResult> {
  const patch: Record<string, unknown> = {};
  const wrote: string[] = [];
  // Only `title` today — the one event-record field the designer can already
  // edit generically (the rename control every doc type shares). Setup's own
  // sections push through their own ops once stage 3 builds them.
  if (doc.name && doc.name !== origin.label) { patch.title = doc.name; wrote.push("title"); }
  if (!wrote.length) return { wrote: [], held: null };

  try {
    await creation.updateEvent(origin.id, patch as unknown as Parameters<typeof creation.updateEvent>[1]);
    return { wrote, held: null };
  } catch {
    return { wrote: [], held: "the event rejected the write — the edit is held in the journeys store" };
  }
}

/**
 * A One-Stop journey writes its whole page back — blocks, header, settings.
 *
 * Unlike a queue or an event, where the designer edits a handful of fields on a
 * record owned elsewhere, the One-Stop page IS the document: there is no other
 * editor for it, so a partial write-back would be the only write it ever gets.
 * Saves the DRAFT; publishing stays its own act.
 */
async function writeBackOneStop(doc: Doc, origin: NonNullable<Doc["origin"]>): Promise<WriteBackResult> {
  const page = doc.pages.find((p) => p.id === "onestop") ?? doc.pages[0];
  if (!page) return { wrote: [], held: null };
  const s = page.osSettings ?? {};
  const title = (doc.name || "").trim() || "One-Stop";
  /* THE STORED PAGE'S OWN SETTINGS COME THROUGH THE WRITE-BACK (2026-09-16).
     `layout` / `showHeader` / `showFooter` are the OneStop tab's Page layout and
     Header and footer controls, and they live on the document — so writing the
     contract defaults here reverted an author's choice every time the same page
     was saved from this canvas. Read the page, spread what it carries, and
     default only when there is no page YET, the same way onestop-tab's
     `buildDoc` does.

     A 404 IS "NO PAGE YET" AND DEFAULTS; ANY OTHER REFUSAL HOLDS THE WRITE.
     They are not the same answer. A transient read error used to fall through to
     the defaults below, so a save still reported success while silently reverting
     the author's layout and chrome toggles — the exact defect the stored-settings
     read exists to fix, just moved into the unhappy path. This object is typed as
     the OUTPUT shape, where `settings` is present and its defaulted fields with
     it, so there is no "omit the key and let the server keep what it has" to
     reach for: not writing is the only honest option, and the overlay already
     holds the edit. */
  let stored: Awaited<ReturnType<typeof getOneStopPage>>["settings"] | null = null;
  try {
    stored = (await getOneStopPage(origin.id)).settings ?? null;
  } catch (err) {
    if ((err as { status?: unknown } | null)?.status !== 404) {
      return { wrote: [], held: "the portal page could not be read, so its settings were not overwritten — the edit is held in the journeys store" };
    }
    stored = null;
  }
  /* LOCALE AND ITS SUPPORTED SET ARE RESOLVED TOGETHER, NOT INDEPENDENTLY.
     The contract's `superRefine` requires the default locale to be a member of
     `supportedLocales`, and two `??` chains reading different sources can break
     that: a doc carrying `locale: "fr-FR"` and no supported set, saved against a
     stored page whose set omits fr-FR, produced a payload the server refuses with
     an opaque validation error. So the set is derived first and the locale is
     guaranteed to be in it — a locale that is not already a member widens the set
     rather than silently changing the locale, which is the one of the two the
     author actually asked for. */
  const locale = s.locale ?? stored?.locale ?? "en-US";
  const supported = s.supportedLocales ?? stored?.supportedLocales ?? [locale];
  const supportedLocales = supported.includes(locale) ? supported : [locale, ...supported];
  try {
    await saveOneStopDraft(origin.id, {
      title,
      header: { eyebrow: null, title, subtitle: page.s || null },
      blocks: (page.osBlocks ?? []).map((b) => ({
        id: b.id, type: b.type, version: b.version,
        ...(b.visibility && b.visibility !== "always" ? { visibility: b.visibility } : {}),
        config: b.config ?? {},
      })) as never,
      settings: {
        /* layout / showHeader / showFooter are required on the settings object
           (JAI-1473), so they are present either from the stored page or from the
           contract defaults below — never omitted, which this object's OUTPUT
           shape would reject, and never re-decided here (2026-09-16). */
        ...(stored ?? {
          navLabel: null,
          locale: "en-US",
          supportedLocales: ["en-US"],
          timeZone: tzContext().zone,
          layout: "get-help-first",
          showHeader: true,
          showFooter: true,
        }),
        navLabel: s.navLabel ?? stored?.navLabel ?? null,
        locale,
        supportedLocales,
        /* Not "UTC" — see the note in nj-attach's attachOneStop. The page's
           civil times are read in this zone; a guess publishes the wrong hour. */
        timeZone: s.timeZone ?? stored?.timeZone ?? tzContext().zone,
        /* WHERE JOURNEYS OPEN (KP, 2026-09-17): "tab" (default) or "same". Not on
           the settings contract yet — request filed with this change. Written
           only when the author chose, so a strict schema is not handed a key it
           does not know until it does; the portal reads it defensively. */
        ...((s.journeyTarget === "same" || s.journeyTarget === "tab") ? { journeyTarget: s.journeyTarget } : {}),
      } as never,
    });
    return { wrote: ["page"], held: null };
  } catch {
    return { wrote: [], held: "the portal rejected the write — the edit is held in the journeys store" };
  }
}

export async function writeBackDoc(doc: Doc): Promise<WriteBackResult> {
  const origin = doc.origin;
  if (!origin) return { wrote: [], held: NO_OP.local! };
  if (origin.kind === "queue") return writeBackQueue(doc, origin);
  if (origin.kind === "event") return writeBackEvent(doc, origin);
  if (origin.kind === "onestop") return writeBackOneStop(doc, origin);
  return { wrote: [], held: NO_OP[origin.kind] ?? NO_OP.local! };
}

/** True for origins that can take a real write today (drives the UI wording). */
export const WRITES_BACK = (kind: string | undefined): boolean => kind === "queue" || kind === "event" || kind === "onestop";

/** Human noun for the write-back toast ("saving onto the queue/event"). */
export const OWNER_NOUN: Record<string, string> = { queue: "queue", event: "event", onestop: "One-Stop page" };
